import { supabase } from '../lib/supabase.js';

export class BookingService {
  // Create a booking after successful payment
  static async createBooking(bookingData) {
    try {
      const { data, error } = await supabase?.from('bookings')?.insert([{
          event_id: bookingData?.eventId,
          ticket_id: bookingData?.ticketId,
          attendee_id: bookingData?.attendeeId,
          quantity: bookingData?.quantity || 1,
          total_amount: bookingData?.totalAmount,
          attendee_info: {
            ...bookingData?.attendeeInfo,
            paymentIntentId: bookingData?.paymentIntentId
          },
          booking_status: 'confirmed',
          payment_status: bookingData?.paymentStatus || 'succeeded',
          booking_reference: bookingData?.bookingReference
        }])?.select(`
          *,
          event:events!bookings_event_id_fkey(
            id,
            title,
            event_date,
            location,
            venue_name
          ),
          ticket:event_tickets!bookings_ticket_id_fkey(
            id,
            ticket_name,
            price,
            ticket_type
          ),
          attendee:user_profiles!bookings_attendee_id_fkey(
            id,
            full_name,
            email
          )
        `)?.single();

      if (error) {
        console.error('Error creating booking:', error);
        return { data: null, error: error?.message };
      }

      // Update ticket quantity sold
      if (data) {
        // First get the current quantity_sold
        const { data: ticketData, error: fetchError } = await supabase?.from('event_tickets')?.select('quantity_sold')?.eq('id', bookingData?.ticketId)?.single();

        if (!fetchError && ticketData) {
          const newQuantitySold = (ticketData?.quantity_sold || 0) + (bookingData?.quantity || 1);
          
          const { error: updateError } = await supabase?.from('event_tickets')?.update({
              quantity_sold: newQuantitySold
            })?.eq('id', bookingData?.ticketId);

          if (updateError) {
            console.error('Error updating ticket quantity:', updateError);
            // Don't fail the booking creation for this error
          }
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Unexpected error in createBooking:', error);
      return { data: null, error: error?.message };
    }
  }

  // Get booking by ID with QR code data
  static async getBookingById(bookingId) {
    try {
      const { data, error } = await supabase?.from('bookings')?.select(`
          *,
          event:events!bookings_event_id_fkey(
            id,
            title,
            event_date,
            location,
            venue_name
          ),
          ticket:event_tickets!bookings_ticket_id_fkey(
            id,
            ticket_name,
            price,
            ticket_type
          ),
          attendee:user_profiles!bookings_attendee_id_fkey(
            id,
            full_name,
            email
          )
        `)?.eq('id', bookingId)?.single();

      if (error) {
        console.error('Error fetching booking:', error);
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Unexpected error in getBookingById:', error);
      return { data: null, error: error?.message };
    }
  }

  // Get booking by reference (for QR code lookup)
  static async getBookingByReference(bookingReference) {
    try {
      const { data, error } = await supabase?.from('bookings')?.select(`
          *,
          event:events!bookings_event_id_fkey(
            id,
            title,
            event_date,
            location,
            venue_name
          ),
          ticket:event_tickets!bookings_ticket_id_fkey(
            id,
            ticket_name,
            price,
            ticket_type
          ),
          attendee:user_profiles!bookings_attendee_id_fkey(
            id,
            full_name,
            email
          )
        `)?.eq('booking_reference', bookingReference)?.single();

      if (error) {
        console.error('Error fetching booking by reference:', error);
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Unexpected error in getBookingByReference:', error);
      return { data: null, error: error?.message };
    }
  }

  // Get user bookings
  static async getUserBookings(userId) {
    try {
      const { data, error } = await supabase?.from('bookings')?.select(`
          *,
          event:events!bookings_event_id_fkey(
            id,
            title,
            event_date,
            location,
            venue_name,
            image_url
          ),
          ticket:event_tickets!bookings_ticket_id_fkey(
            id,
            ticket_name,
            price,
            ticket_type
          )
        `)?.eq('attendee_id', userId)?.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching user bookings:', error);
        return { data: [], error: error?.message };
      }

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Unexpected error in getUserBookings:', error);
      return { data: [], error: error?.message };
    }
  }

  // Update booking status
  static async updateBookingStatus(bookingId, status) {
    try {
      const { data, error } = await supabase?.from('bookings')?.update({ 
          booking_status: status,
          updated_at: new Date()?.toISOString()
        })?.eq('id', bookingId)?.select()?.single();

      if (error) {
        console.error('Error updating booking status:', error);
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Unexpected error in updateBookingStatus:', error);
      return { data: null, error: error?.message };
    }
  }

  // Generate QR code data for ticket
  static generateQRCodeData(booking) {
    return {
      id: booking?.id,
      reference: booking?.booking_reference,
      eventId: booking?.event_id,
      eventTitle: booking?.event?.title,
      attendeeName: booking?.attendee?.full_name,
      attendeeEmail: booking?.attendee?.email,
      ticketType: booking?.ticket?.ticket_name,
      quantity: booking?.quantity,
      eventDate: booking?.event?.event_date,
      location: booking?.event?.location,
      venue: booking?.event?.venue_name,
      checkInUrl: `${window.location?.origin}/checkin/${booking?.booking_reference}`,
      validationCode: booking?.booking_reference
    };
  }

  // Format booking for display
  static formatBooking(booking) {
    return {
      id: booking?.id,
      reference: booking?.booking_reference,
      eventTitle: booking?.event?.title,
      eventDate: booking?.event?.event_date,
      eventLocation: booking?.event?.location,
      eventVenue: booking?.event?.venue_name,
      eventImage: booking?.event?.image_url,
      ticketName: booking?.ticket?.ticket_name,
      ticketType: booking?.ticket?.ticket_type,
      ticketPrice: booking?.ticket?.price,
      quantity: booking?.quantity,
      totalAmount: booking?.total_amount,
      attendeeName: booking?.attendee?.full_name,
      attendeeEmail: booking?.attendee?.email,
      attendeeInfo: booking?.attendee_info,
      bookingStatus: booking?.booking_status,
      paymentStatus: booking?.payment_status,
      createdAt: booking?.created_at,
      qrCodeData: this.generateQRCodeData(booking)
    };
  }
}

export default BookingService;